/**
 * Progress bar for async submit flows. Use with useSubmitProgress hook.
 *
 * @param {number} progress - 0–100
 * @param {'idle' | 'loading' | 'done' | 'error'} status
 * @param {string} [loadingLabel] - Shown while status is 'loading'
 * @param {string} [completingLabel] - Shown when status is 'done' and progress < 100
 * @param {boolean} [showPulseDots=true] - Show bouncing dots while loading
 * @param {string} [className] - Wrapper class
 */
export function SubmitProgressBar({
  progress,
  status,
  loadingLabel = 'Loading…',
  completingLabel = 'Almost there…',
  showPulseDots = true,
  className = '',
}) {
  const visible = status === 'loading' || (status === 'done' && progress < 100);
  if (!visible) return null;

  const barColor = status === 'done' ? 'bg-green-5' : 'bg-primary-5';

  return (
    <div className={className}>
      <div className="flex justify-between mb-1">
        <span className="text-neutral-7 text-xl font-medium">
          {status === 'loading' && loadingLabel}
          {status === 'done' && completingLabel}
        </span>
        <span className="text-neutral-7 text-xl font-medium">{progress}%</span>
      </div>
      <div className="w-full bg-neutral-4 rounded-full h-1.5 overflow-hidden">
        <div
          className={`h-1.5 rounded-full transition-all duration-200 ease-out ${barColor}`}
          style={{ width: `${progress}%` }}
        />
      </div>
      {showPulseDots && status === 'loading' && (
        <div className="flex gap-1 mt-2">
          {[0, 1, 2].map(i => (
            <span
              key={i}
              className="w-1.5 h-1.5 rounded-full bg-primary-5 animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
